// Standard library imports
import React, { useState, useEffect } from 'react';

// Internal module imports
import TabsComponent from '../../../components/Tabs/Tabs';
import Typography from '../../../components/Typography/Typography';
import ThreeStateToggle from '../../../components/Inputs/ThreeStateToggle/ThreeStateToggle';
import { THREE_STATE_TOGGLE_LABELS } from '../../../constants';
import { Form, Formik } from 'formik';
import { Input } from '../../../components/Inputs/Input';
import allDeviceClasses from '../../../styles/AllDevices.module.css';
import { useLoader } from '../../../hooks';
import { toast } from 'react-toastify';
import _ from 'lodash';
import { forceFactorValidation } from '../../../validations/Edge/edgeDevice';
import edgeClasses from '../Edge.module.css';
import { InstanceService } from '../../../services/instanceService';

const modalStyle = {
    width: '28vw',
    display: 'flex',
    flexDirection: 'column',
    gap: '3vh',
};

const FORCE_FACTOR_OPTIONS = {
    left: {
        title: 'Off',
        value: 'left',
    },
    center: {
        title: 'Default',
        value: 'center',
    },
    right: {
        title: 'Custom',
        value: 'right',
    },
};

const ForceFactorActivation = ({ instance, market, setForceModal, getEdgeData }) => {
    const [startLoader, stopLoader] = useLoader();

    const [forceFactor, setForceFactor] = useState({});
    const [selectedState, setSelectedState] = useState({});

    useEffect(() => {
        getInstanceDetails();
    }, []);

    const getInstanceDetails = () => {
        if (instance?._id) {
            InstanceService.GetInstanceDetails(instance?._id, startLoader, handleInstanceDetailsSuccess, handleError, stopLoader);
        }
    };

    const handleError = (err) => {
        console.log(err);
        if (err && err.response) toast.error(err.response.data.message);
    };

    const handleInstanceDetailsSuccess = ({ data }) => {
        if (market.length) {
            const temp = {};
            market.map((m) => {
                if (_.get(data, ['data', 'forceFactorStatus', m])) {
                    _.set(temp, m, {
                        status: _.get(data, ['data', 'forceFactorStatus', m, 'status']),
                        value: _.get(data, ['data', 'forceFactorStatus', m, 'value'], ''),
                        adjustmentFactor: _.get(data, ['data', 'forceFactorStatus', m, 'adjustmentFactor'], ''),
                    });
                } else {
                    _.set(temp, m, {
                        status: 'OFF',
                        value: 1,
                        adjustmentFactor: 0,
                    });
                }
            });
            setForceFactor(temp);
            const tempActivationStatus = {};
            market.map((m) => {
                let temp = 'center';
                switch (_.get(data, ['data', 'forceAcivationStatus', m])) {
                    case 'DEACTIVATE': {
                        temp = 'left';
                        break;
                    }
                    case 'DEFAULT': {
                        temp = 'center';
                        break;
                    }
                    case 'ACTIVATE': {
                        temp = 'right';
                        break;
                    }
                    default: {
                        break;
                    }
                }
                _.set(tempActivationStatus, m, temp);
            });

            setSelectedState(tempActivationStatus);
        }
    };

    const onChangeForceState = (value, m) => {
        if (instance?._id && m) {
            let state = 'DEFAULT';
            switch (value) {
                case 'left': {
                    state = 'DEACTIVATE';
                    break;
                }
                case 'center': {
                    state = 'DEFAULT';
                    break;
                }
                case 'right': {
                    state = 'ACTIVATE';
                    break;
                }
                default: {
                    state = 'DEFAULT';
                    break;
                }
            }

            const params = {
                forceAcivationStatus: state,
                id: instance?._id,
                market: m,
            };

            InstanceService.ForceActivation(params, startLoader, handleForceStatusSuccess, handleError, stopLoader);
        }
    };

    const handleForceStatusSuccess = () => {
        toast.success('Activation state updated!');
        getInstanceDetails();
        getEdgeData();
    };

    const updateForceFactor = (values) => {
        if (instance?._id && market?.length) {
            const payload = {
                forceFactorStatus: { ...values },
                id: instance?._id,
            };
            InstanceService.UpdateForceFactor(payload, startLoader, handleForceFactorSuccess, handleError, stopLoader);
        }
    };

    const handleForceFactorSuccess = () => {
        toast.success('Factor state updated!');
        getEdgeData();
    };

    const tabs = [
        {
            name: 'Force Activation',
            component: instance?.name ? (
                <div style={modalStyle}>
                    <Typography size={'14'} content={`Instance: ${instance?.name}`} />
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '0.5vw' }}>
                        {market.map((m, idx) => (
                            <div>
                                <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: '0.2vw' }}>
                                    <Typography content={m} subtext={true} />
                                    <ThreeStateToggle
                                        labels={THREE_STATE_TOGGLE_LABELS}
                                        onChange={(e) => {
                                            onChangeForceState(e, m);
                                        }}
                                        defaultValue={_.get(selectedState, m, 'center')}
                                        id={idx}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>

                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'end',
                        }}
                    >
                        <button
                            onClick={() => {
                                setForceModal(false);
                            }}
                            style={{ width: '7vw', margin: '0' }}
                            className="btn-secondary"
                            type="button"
                        >
                            Close
                        </button>
                    </div>
                </div>
            ) : (
                <div style={modalStyle}>
                    <Typography size={'14'} content={`Instance Not Configured`} />
                </div>
            ),
        },
        {
            name: 'Force Factor',
            component: instance?.name ? (
                <div style={modalStyle}>
                    <Typography size={'14'} content={`Instance: ${instance?.name}`} />
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                        }}
                    >
                        <Formik initialValues={forceFactor} onSubmit={updateForceFactor} enableReinitialize validationSchema={forceFactorValidation(market)}>
                            {({ errors, touched, values, isValidating, ...props }) => (
                                <Form style={{ width: '100%' }}>
                                    {market.map((m, idx) => (
                                        <div>
                                            <div className={edgeClasses.ForceFactorContrainer}>
                                                <Typography content={m} subtext={true} />
                                                <div>
                                                    <ThreeStateToggle
                                                        labels={FORCE_FACTOR_OPTIONS}
                                                        onChange={(e) => {
                                                            let status = 'OFF';
                                                            switch (e) {
                                                                case 'left': {
                                                                    status = 'OFF';
                                                                    break;
                                                                }
                                                                case 'center': {
                                                                    status = 'DEFAULT';
                                                                    break;
                                                                }
                                                                case 'right': {
                                                                    status = 'CUSTOM';
                                                                    break;
                                                                }
                                                                default: {
                                                                    status = 'OFF';
                                                                    break;
                                                                }
                                                            }
                                                            props.setFieldValue(`${m}.status`, status);
                                                        }}
                                                        defaultValue={
                                                            _.get(forceFactor, [m, 'status']) === 'OFF'
                                                                ? 'left'
                                                                : _.get(forceFactor, [m, 'status']) === 'DEFAULT'
                                                                  ? 'center'
                                                                  : 'right'
                                                        }
                                                        id={`${idx}-factor`}
                                                    />
                                                </div>
                                                {_.get(values, [m, 'status']) === 'CUSTOM' && (
                                                    <div>
                                                        <div className={allDeviceClasses.InputContainer}>
                                                            <div className={allDeviceClasses.FieldControl2}>
                                                                <label>
                                                                    Capacity Factor <span className="required">*</span>
                                                                </label>
                                                                <Input name={`${m}.value`} type="number" style={{ marginTop: '0' }} />
                                                            </div>
                                                            <div className={allDeviceClasses.FieldControl2}>
                                                                <label>
                                                                    Adjustment Factor <span className="required">*</span>
                                                                </label>
                                                                <Input name={`${m}.adjustmentFactor`} type="number" style={{ marginTop: '0' }} />
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                    <div className={allDeviceClasses.ButtonContainer}>
                                        <div>
                                            <button
                                                onClick={() => {
                                                    setForceModal(false);
                                                }}
                                                className="btn-secondary"
                                                type="button"
                                            >
                                                Close
                                            </button>
                                        </div>

                                        <div>
                                            <button className="btn-primary" type="submit">
                                                Save
                                            </button>
                                        </div>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </div>
                </div>
            ) : (
                <div style={modalStyle}>
                    <Typography size={'14'} content={`Instance Not Configured`} />
                </div>
            ),
        },
    ];

    return (
        <div style={{ height: 'auto', width: 'auto' }}>
            <TabsComponent tabs={tabs} />
        </div>
    );
};

export default ForceFactorActivation;
